// Navy + teal palette for the Nurse Dashboard, ported from the Caregiver Hub
// reference (src/lib/ipd-theme.ts) and adapted to MUI v5.
import { createTheme } from "@mui/material/styles";

export const NURSE_COLORS = {
  primary: "#1A2B4B",
  primaryDark: "#0D1B2A",
  primaryLight: "#3D5A80",
  accent: "#21CDC0",
  accentSoft: "#EBFAF9",
  pageBg: "#F5F7FA",
  cardBg: "#FFFFFF",
  border: "#E2E8F0",
  textPrimary: "#1A2B4B",
  textSecondary: "#64748B",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#2563EB",
};

// A scoped theme so MUI `color="primary"` etc. render in the navy/teal palette
// regardless of the surrounding DoctorLayout app theme.
export const nurseTheme = createTheme({
  palette: {
    primary: { main: NURSE_COLORS.primary, dark: NURSE_COLORS.primaryDark, light: NURSE_COLORS.primaryLight },
    secondary: { main: NURSE_COLORS.accent },
    background: { default: NURSE_COLORS.pageBg, paper: NURSE_COLORS.cardBg },
    text: { primary: NURSE_COLORS.textPrimary, secondary: NURSE_COLORS.textSecondary },
    success: { main: NURSE_COLORS.success },
    warning: { main: NURSE_COLORS.warning },
    error: { main: NURSE_COLORS.danger },
    info: { main: NURSE_COLORS.info },
    divider: NURSE_COLORS.border,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
  },
});

export const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

// Map a Low/Moderate/High risk string to an MUI chip color.
export const riskColor = (risk) =>
  risk === "High" ? "error" : risk === "Moderate" ? "warning" : "success";

// Deterministic per-person avatar color for any list of nurses (Bed
// Assignments' Nurses panel, the Week Grid, the Nurse Directory) — the same
// nurse always gets the same color across every screen. Explicitly NOT used
// for the current-user badge in NurseDashboardShell's sidebar (that stays the
// fixed accent teal — it's a "you are here" indicator, not a roster listing)
// or for any patient avatar.
const AVATAR_PALETTE = [
  "#0F766E", // teal
  "#7C3AED", // violet
  "#2563EB", // blue
  "#D97706", // amber
  "#DB2777", // pink
  "#059669", // emerald
  "#4F46E5", // indigo
  "#DC2626", // red
];

export const nurseAvatarColor = (key = "") => {
  let hash = 0;
  const str = String(key);
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};
